/**
 * デバイス Drawer の「MDM」タブ。Apple MDM(同梱の NanoMDM)に登録された Mac / iPhone / iPad
 * を操作する。
 *
 * **出せるコマンドはサーバーが決める**(`request_types`。OS と監視モードで絞った結果)。
 * iPhone / iPad はロック・消去に PIN を取らず、再起動は監視モードの端末だけ、
 * パスコード解除(ClearPasscode)は iPhone / iPad だけ。
 *
 * **押した直後は「届いた」ではない。** コマンドは NanoMDM に積まれ、端末が取りに来た
 * ときに実行される。だから状態は `pushed`(NanoMDM が受けた)から始まり、端末の応答が
 * Webhook で届いた時点で `acknowledged` / `error` / `notnow` に変わる。開いている間だけ
 * 5 秒ごとに見に行く。
 */
import { useMutation } from "@tanstack/react-query";
import {
  Alert,
  Button,
  Card,
  Checkbox,
  Descriptions,
  Form,
  Input,
  Modal,
  Popconfirm,
  Space,
  Table,
  Tag,
  Tooltip,
  Typography,
  Upload,
  message,
} from "antd";
import type { ColumnsType } from "antd/es/table";
import type { UploadFile } from "antd/es/upload/interface";
import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";

import type { DeviceDetail, MdmCommand, MdmEnrollment, MdmRequestType } from "../../lib/api";
import { jst } from "../../lib/format";
import { useApi } from "../../lib/useApi";
import { useQueryClient } from "@tanstack/react-query";

import { errorText, useDeviceCommands, useInvalidateMdm, useMdmPerms } from "./hooks";

/** 状態の見え方。**push_failed は「端末に届いていない」**なので赤で出す。 */
const STATUS: Record<string, { color: string; label: string }> = {
  queued: { color: "default", label: "積んだ" },
  pushed: { color: "blue", label: "送った(応答待ち)" },
  push_failed: { color: "red", label: "送れなかった" },
  acknowledged: { color: "green", label: "完了" },
  error: { color: "red", label: "端末がエラー" },
  notnow: { color: "orange", label: "端末が今は無理" },
};

const TYPE_LABEL: Record<string, string> = {
  DeviceInformation: "端末情報",
  InstalledApplicationList: "アプリ一覧",
  ProfileList: "プロファイル一覧",
  RestartDevice: "再起動",
  InstallProfile: "プロファイル配布",
  RemoveProfile: "プロファイル削除",
  DeviceLock: "ロック",
  EraseDevice: "ワイプ",
  ClearPasscode: "パスコード解除",
};

/** 登録の OS の見え方。`unknown` は名乗りが読めなかった端末(読むだけのコマンドしか出ない)。 */
const PLATFORM_LABEL: Record<string, string> = {
  macos: "macOS",
  ios: "iOS",
  ipados: "iPadOS",
  unknown: "不明",
};

/** 画面の文言に使う呼び名(「この Mac を再起動する」など)。 */
function nounOf(platform: string): string {
  if (platform === "ios") return "iPhone";
  if (platform === "ipados") return "iPad";
  if (platform === "macos") return "Mac";
  return "端末";
}

function supervisedText(e: MdmEnrollment): string {
  if (e.supervised === true) return "監視モード";
  if (e.supervised === false) return "監視なし";
  return "未確認(端末情報更新で分かる)";
}

/** DeviceInformation の応答から画面に出すのは**電池と監視モードだけ**(電話番号などは出さない)。 */
function batteryText(device: DeviceDetail): string | null {
  const info = device.inventory?.device_info as Record<string, unknown> | undefined;
  const level = info?.BatteryLevel;
  if (typeof level !== "number" || level < 0) return null;
  return `${Math.round(level * 100)}%`;
}

function statusTag(v: string) {
  const s = STATUS[v] ?? { color: "default", label: v };
  return <Tag color={s.color}>{s.label}</Tag>;
}

/** 応答の中身。plist をそのまま入れてあるので整形して出すだけ。 */
function ResponseView({ row }: { row: MdmCommand }) {
  const body = Object.keys(row.response ?? {}).length ? row.response : null;
  return (
    <Space direction="vertical" size={4} style={{ width: "100%" }}>
      {row.error && <Alert type="error" showIcon message={row.error} />}
      <Typography.Text type="secondary" style={{ fontSize: 12 }}>
        CommandUUID: {row.command_uuid}
      </Typography.Text>
      <pre style={{ margin: 0, maxHeight: 240, overflow: "auto", fontSize: 12 }}>
        {body ? JSON.stringify(body, null, 2) : "応答なし"}
      </pre>
    </Space>
  );
}

type Pending = { type: "InstallProfile" | "RemoveProfile" | "DeviceLock" | "EraseDevice" };

export function MdmTab({ device, open }: { device: DeviceDetail; open: boolean }) {
  const api = useApi();
  const invalidate = useInvalidateMdm();
  const { canWrite, canAdmin } = useMdmPerms();
  const query = useDeviceCommands(device.id, open);
  const [pending, setPending] = useState<Pending | null>(null);
  const [form] = Form.useForm();
  const [files, setFiles] = useState<UploadFile[]>([]);
  const qc = useQueryClient();

  // 端末情報(DeviceInformation)は送った**後で**端末が応える。ポーリングで完了を見つけた
  // 時点で端末の詳細(電池・監視モード)と台帳の行を取り直す(MOB1 中2)。
  // 初回に見えていた完了分は「新しく完了した」ではないので数えない
  const seenDone = useRef<Set<number> | null>(null);
  useEffect(() => {
    if (!query.data) return;
    const done = new Set(
      query.data.items
        .filter((r) => r.request_type === "DeviceInformation" && r.status === "acknowledged")
        .map((r) => r.id),
    );
    const prev = seenDone.current;
    seenDone.current = done;
    if (prev && [...done].some((id) => !prev.has(id))) {
      void qc.invalidateQueries({ queryKey: ["mdm", "device", device.id] });
      void qc.invalidateQueries({ queryKey: ["mdm", "devices"] });
    }
  }, [query.data, device.id, qc]);

  const enrollment = query.data?.enrollment ?? null;
  const rows = query.data?.items ?? [];
  const total = query.data?.total ?? 0;
  const platform = enrollment?.platform ?? "macos";
  const isMac = platform === "macos";
  const noun = nounOf(platform);
  // 古いサーバー(request_types を返さない)では今までどおり Mac として全部出す
  const types: MdmRequestType[] | null = query.data?.request_types ?? null;
  const typeOf = (t: string) => types?.find((x) => x.request_type === t);
  const shown = (t: string) => types === null || typeOf(t) !== undefined;
  const reasonOf = (t: string) => {
    const x = typeOf(t);
    return x && !x.available ? x.reason || "この端末には出せない" : null;
  };

  const send = useMutation({
    mutationFn: (body: {
      request_type: string;
      params?: Record<string, unknown>;
      confirm?: string;
    }) => api.enqueueDeviceCommand(device.id, body),
    onSuccess: (row) => {
      if (row.status === "push_failed") {
        void message.warning(`NanoMDM に渡せなかった: ${row.error}`);
      } else {
        void message.success(`${TYPE_LABEL[row.request_type] ?? row.request_type}を送った`);
      }
      setPending(null);
      form.resetFields();
      setFiles([]);
      invalidate();
      void query.refetch();
    },
    onError: (e) => void message.error(errorText(e)),
  });

  const push = useMutation({
    mutationFn: () => api.pushDevice(device.id),
    onSuccess: () => {
      void message.success("端末に取りに来るよう促した");
      void query.refetch();
    },
    onError: (e) => void message.error(errorText(e)),
  });

  const columns: ColumnsType<MdmCommand> = [
    {
      title: "種類",
      dataIndex: "request_type",
      width: 170,
      render: (v: string) => TYPE_LABEL[v] ?? v,
    },
    { title: "状態", dataIndex: "status", width: 150, render: (v: string) => statusTag(v) },
    { title: "実行者", dataIndex: "created_by", width: 160, },
    {
      title: "送信",
      dataIndex: "created_at",
      width: 180,
      render: (v: string | null) => jst(v),
    },
    {
      title: "更新",
      dataIndex: "updated_at",
      width: 180,
      render: (v: string | null) => jst(v),
    },
  ];

  const simple = (request_type: string) => () => send.mutate({ request_type });

  if (!enrollment) {
    return (
      <Space direction="vertical" size="middle" style={{ width: "100%" }}>
        <Alert
          type="info"
          showIcon
          message="この端末はまだ Apple MDM に登録されていない"
          description={
            <span>
              <Link to="/mdm/agents">エージェント連携</Link>
              から登録プロファイルを落として Mac / iPhone / iPad に入れると、ここに操作が出る。
            </span>
          }
        />
        {query.isError && <Alert type="error" showIcon message={errorText(query.error)} />}
      </Space>
    );
  }

  return (
    <Space direction="vertical" size="middle" style={{ width: "100%" }}>
      <Descriptions size="small" column={1} bordered data-testid="mdm-enrollment">
        <Descriptions.Item label="OS">
          <Tag data-testid="mdm-platform">{PLATFORM_LABEL[platform] ?? platform}</Tag>
        </Descriptions.Item>
        {!isMac && (
          <Descriptions.Item label="監視モード">{supervisedText(enrollment)}</Descriptions.Item>
        )}
        {batteryText(device) && (
          <Descriptions.Item label="バッテリー">{batteryText(device)}</Descriptions.Item>
        )}
        <Descriptions.Item label="UDID">{enrollment.udid}</Descriptions.Item>
        <Descriptions.Item label="登録">{jst(enrollment.enrolled_at)}</Descriptions.Item>
        <Descriptions.Item label="最後の応答">{jst(enrollment.last_seen)}</Descriptions.Item>
        <Descriptions.Item label="push トークン">
          {enrollment.checked_out_at ? (
            <Tag color="red">管理から外れた({jst(enrollment.checked_out_at)})</Tag>
          ) : enrollment.token_present ? (
            <Tag color="green">あり</Tag>
          ) : (
            <Tag color="orange">まだ届いていない</Tag>
          )}
        </Descriptions.Item>
        <Descriptions.Item label="Topic">{enrollment.topic || "—"}</Descriptions.Item>
      </Descriptions>

      {canWrite && !enrollment.checked_out_at && (
        <Card size="small" title="この端末に送る">
          <Space wrap>
            <Button onClick={simple("DeviceInformation")} loading={send.isPending}>
              端末情報更新
            </Button>
            <Button onClick={simple("InstalledApplicationList")} loading={send.isPending}>
              アプリ一覧
            </Button>
            <Button onClick={simple("ProfileList")} loading={send.isPending}>
              プロファイル一覧
            </Button>
            {shown("InstallProfile") && (
              <Button onClick={() => setPending({ type: "InstallProfile" })}>
                プロファイル配布
              </Button>
            )}
            {shown("RemoveProfile") && (
              <Button onClick={() => setPending({ type: "RemoveProfile" })}>
                プロファイル削除
              </Button>
            )}
            {shown("RestartDevice") &&
              (reasonOf("RestartDevice") ? (
                // 押せない理由を出す(非監視の iPhone / iPad は MDM から再起動できない)
                <Tooltip title={reasonOf("RestartDevice")}>
                  <Button danger disabled data-testid="mdm-restart">
                    再起動
                  </Button>
                </Tooltip>
              ) : (
                <Popconfirm
                  title={`この ${noun} を再起動する`}
                  description="作業中のものは保存されない"
                  okText="再起動する"
                  cancelText="やめる"
                  onConfirm={simple("RestartDevice")}
                >
                  <Button danger data-testid="mdm-restart">
                    再起動
                  </Button>
                </Popconfirm>
              ))}
            {canAdmin && (
              <>
                {shown("DeviceLock") && (
                  <Button danger onClick={() => setPending({ type: "DeviceLock" })}>
                    ロック
                  </Button>
                )}
                {shown("EraseDevice") && (
                  <Button
                    danger
                    disabled={!device.serial}
                    title={
                      device.serial
                        ? undefined
                        : "製造番号が分からない端末は消去できない(先に端末情報更新を送る)"
                    }
                    onClick={() => setPending({ type: "EraseDevice" })}
                  >
                    ワイプ
                  </Button>
                )}
                {shown("ClearPasscode") && (
                  <Popconfirm
                    title={`この ${noun} のパスコードを外す`}
                    description="端末は誰でも開ける状態になる。利用者に新しいパスコードを設定してもらう"
                    okText="外す"
                    cancelText="やめる"
                    onConfirm={simple("ClearPasscode")}
                    disabled={!enrollment.unlock_token_present}
                  >
                    <Button
                      danger
                      disabled={!enrollment.unlock_token_present}
                      title={
                        enrollment.unlock_token_present
                          ? undefined
                          : "端末から解除用のトークンが届いていない(登録し直すと届く)"
                      }
                    >
                      パスコード解除
                    </Button>
                  </Popconfirm>
                )}
              </>
            )}
            <Button onClick={() => push.mutate()} loading={push.isPending}>
              再 push
            </Button>
          </Space>
          <Typography.Paragraph type="secondary" style={{ fontSize: 12, marginBottom: 0 }}>
            送った時点では端末に届いていない。端末が取りに来ると状態が「完了」に変わる
            (スリープ中は「今は無理」のまま)。
          </Typography.Paragraph>
        </Card>
      )}

      {/* 概要タブは表示を切り替えても DOM に残る。E2E が行を数えるときに
          混ざらないよう、この表だけを指せる目印を付けておく */}
      <Card
        size="small"
        title={`コマンド履歴(${total} 件)`}
        data-testid="mdm-commands"
      >
        <Table<MdmCommand>
          rowKey="id"
          size="small"
          columns={columns}
          dataSource={rows}
          loading={query.isFetching && !query.data}
          pagination={false}
          scroll={{ x: "max-content", y: 320 }}
          expandable={{ expandedRowRender: (row) => <ResponseView row={row} /> }}
          locale={{ emptyText: "まだ何も送っていない" }}
        />
        {/* **表に出ているのは先頭ページだけ**(API は既定 50 件でページングする)。
            出ていない行があることを言わないと、増えたのに増えていないように見える */}
        {total > rows.length && (
          <Typography.Paragraph
            type="secondary"
            style={{ fontSize: 12, marginTop: 8, marginBottom: 0 }}
          >
            {`新しい順に ${rows.length} 件だけ表示(全 ${total} 件)`}
          </Typography.Paragraph>
        )}
      </Card>

      <Modal
        open={pending?.type === "InstallProfile"}
        title="プロファイルを配る"
        okText="配る"
        cancelText="やめる"
        confirmLoading={send.isPending}
        onCancel={() => {
          setPending(null);
          setFiles([]);
        }}
        onOk={() => {
          const file = files[0]?.originFileObj;
          if (!file) {
            void message.error("mobileconfig を選ぶ");
            return;
          }
          void file.arrayBuffer().then((buf) => {
            let bin = "";
            new Uint8Array(buf).forEach((b) => (bin += String.fromCharCode(b)));
            send.mutate({ request_type: "InstallProfile", params: { payload: btoa(bin) } });
          });
        }}
      >
        <Upload
          accept=".mobileconfig"
          maxCount={1}
          beforeUpload={() => false}
          fileList={files}
          onChange={(info) => setFiles(info.fileList)}
        >
          <Button>mobileconfig を選ぶ</Button>
        </Upload>
        <Typography.Paragraph type="secondary" style={{ fontSize: 12, marginTop: 8 }}>
          署名していないプロファイルは Mac 側で警告が出る。中身はここには残らない
          (大きさだけ記録する)。
        </Typography.Paragraph>
      </Modal>

      <Modal
        open={pending?.type === "RemoveProfile"}
        title="プロファイルを消す"
        okText="消す"
        cancelText="やめる"
        confirmLoading={send.isPending}
        onCancel={() => setPending(null)}
        onOk={() =>
          void form.validateFields().then((v) =>
            send.mutate({
              request_type: "RemoveProfile",
              params: { identifier: v.identifier },
            }),
          )
        }
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name="identifier"
            label="PayloadIdentifier"
            rules={[{ required: true, message: "「プロファイル一覧」の応答から写す" }]}
          >
            <Input placeholder="com.example.profile" />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        open={pending?.type === "DeviceLock" || pending?.type === "EraseDevice"}
        title={
          pending?.type === "EraseDevice" ? `この ${noun} を消去する` : `この ${noun} をロックする`
        }
        okText={pending?.type === "EraseDevice" ? "消去する" : "ロックする"}
        okButtonProps={{ danger: true }}
        cancelText="やめる"
        confirmLoading={send.isPending}
        onCancel={() => {
          setPending(null);
          form.resetFields();
        }}
        onOk={() =>
          void form.validateFields().then((v) =>
            send.mutate({
              request_type: pending!.type,
              params: isMac
                ? { pin: v.pin, ...(v.lockMessage ? { message: v.lockMessage } : {}) }
                : pending!.type === "DeviceLock"
                  ? {
                      ...(v.lockMessage ? { message: v.lockMessage } : {}),
                      ...(v.phoneNumber ? { phone_number: v.phoneNumber } : {}),
                    }
                  : {
                      preserve_data_plan: !!v.preserveDataPlan,
                      disallow_proximity_setup: !!v.disallowProximitySetup,
                    },
              confirm: v.confirm,
            }),
          )
        }
      >
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 12 }}
          message={
            !isMac
              ? pending?.type === "EraseDevice"
                ? "中身は戻らない。端末は初期状態に戻り、管理からも外れる"
                : "端末の画面がロックされる。解除には利用者のパスコードが要る"
              : pending?.type === "EraseDevice"
                ? "中身は戻らない。解除 PIN を控えてから実行する"
                : "解除には下の PIN が要る。控えてから実行する"
          }
          description={
            <>
              {isMac ? "PIN はここにしか出ない(サーバーには残さない)。" : `${noun} は PIN を使わない。`}
              <br />
              対象: UDID {enrollment.udid}
              {device.serial ? ` / 製造番号 ${device.serial}` : ""}
            </>
          }
        />
        <Form form={form} layout="vertical">
          {isMac && (
            <Form.Item
              name="pin"
              label="解除 PIN(6 桁の数字)"
              rules={[{ required: true, pattern: /^\d{6}$/, message: "6 桁の数字" }]}
            >
              <Input inputMode="numeric" maxLength={6} placeholder="123456" />
            </Form.Item>
          )}
          {pending?.type === "DeviceLock" && (
            <Form.Item name="lockMessage" label="画面に出す文言(任意)">
              <Input maxLength={200} placeholder="情報システム部までご連絡ください" />
            </Form.Item>
          )}
          {!isMac && pending?.type === "DeviceLock" && (
            <Form.Item
              name="phoneNumber"
              label="ロック画面に出す連絡先の電話番号(任意)"
              rules={[{ pattern: /^[0-9+\-() ]{0,30}$/, message: "数字と + - ( ) と空白だけ" }]}
            >
              <Input inputMode="tel" maxLength={30} placeholder="03-1234-5678" />
            </Form.Item>
          )}
          {!isMac && pending?.type === "EraseDevice" && (
            <>
              <Form.Item name="preserveDataPlan" valuePropName="checked" style={{ marginBottom: 4 }}>
                <Checkbox>モバイル通信プラン(eSIM)を残す</Checkbox>
              </Form.Item>
              <Form.Item name="disallowProximitySetup" valuePropName="checked">
                <Checkbox>消去後の「クイックスタート」(近くの端末からの設定)を禁止する</Checkbox>
              </Form.Item>
            </>
          )}
          {pending?.type === "EraseDevice" && (
            <Form.Item
              name="confirm"
              label={`確認のため製造番号(${device.serial})を入力`}
              rules={[
                {
                  required: true,
                  validator: (_, value) =>
                    String(value ?? "").trim() === device.serial
                      ? Promise.resolve()
                      : Promise.reject(new Error("製造番号が一致しない")),
                },
              ]}
            >
              <Input placeholder={device.serial} />
            </Form.Item>
          )}
        </Form>
      </Modal>
    </Space>
  );
}
